import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button, type ButtonProps } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import styles from './toggle-button-group.module.css';

export interface ToggleButtonGroupItem {
  value: string;
  label: ReactNode;
  icon?: ReactNode;
  href?: string;
}

export interface ToggleButtonGroupProps {
  value: string;
  onValueChange?: (value: string) => void;
  items: ToggleButtonGroupItem[];
  'aria-label': string;
  size?: NonNullable<ButtonProps['size']>;
  className?: string;
  itemClassName?: string;
  /** Keep every option on one line. On a phone the group fills the row as tabs. */
  nowrap?: boolean;
}

export function ToggleButtonGroup({
  value,
  onValueChange,
  items,
  'aria-label': ariaLabel,
  size = 'default',
  className,
  itemClassName,
  nowrap = false,
}: ToggleButtonGroupProps) {
  return (
    <div
      className={cn(styles.root, nowrap && styles.rootNowrap, className)}
      role="group"
      aria-label={ariaLabel}
    >
      {items.map((item) => {
        const isActive = value === item.value;
        const content = (
          <>
            {item.icon}
            {item.label}
          </>
        );

        if (item.href) {
          return (
            <Button
              key={item.value}
              asChild
              size={size}
              variant={isActive ? 'default' : 'outline'}
              className={cn(styles.item, itemClassName)}
              aria-current={isActive ? 'page' : undefined}
            >
              <Link to={item.href}>{content}</Link>
            </Button>
          );
        }

        return (
          <Button
            key={item.value}
            type="button"
            size={size}
            variant={isActive ? 'default' : 'outline'}
            className={cn(styles.item, itemClassName)}
            aria-pressed={isActive}
            onClick={() => onValueChange?.(item.value)}
          >
            {content}
          </Button>
        );
      })}
    </div>
  );
}
